// Extracted from AIWorkoutGenerator.js during Phase 4 refactor (sprint 2026-06-20).
import React, { useState, useEffect } from 'react';
import { api } from '../../context/AppContext';
import useExerciseHistory from '../../hooks/useExerciseHistory';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Zap, Loader2, Play, Dumbbell, Clock, Repeat, Timer, ChevronDown, ChevronUp,
  Battery, BatteryLow, BatteryFull, Star, ThumbsUp, Send, Info,
  AlertTriangle, Shield, Save, BookmarkCheck, ExternalLink, History, RotateCcw, Trophy, Crosshair,
  CalendarDays, Layers, Target, Wrench, HeartPulse, BookOpen, Flame, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const QUICK_DIRECTIVE_CHIPS_FR = [
  { id: 'no_jumps', icon: '🚫', label: 'Pas de sauts', phrase: 'Pas de sauts ni de plyométrie' },
  { id: 'no_impact', icon: '🚫', label: 'Pas d\u2019impact', phrase: 'Aucun exercice à impact (low-impact uniquement)' },
  { id: 'no_pushups', icon: '🚫', label: 'Pas de push-ups', phrase: 'Pas de push-ups (variantes sur banc/poulie acceptées)' },
  { id: 'no_burpees', icon: '🚫', label: 'Pas de burpees', phrase: 'Pas de burpees' },
  { id: 'focus_core', icon: '💪', label: 'Focus core', phrase: 'Mets l\u2019accent sur le core (gainage, anti-rotation, transverse)' },
  { id: 'more_legs', icon: '🦵', label: 'Plus de jambes', phrase: 'Augmente le volume jambes (au moins 1 jour dédié + 1 mvt jambes par jour)' },
  { id: 'slow_tempo', icon: '\u23F1\uFE0F', label: 'Tempo lent', phrase: 'Privilégie un tempo lent et contrôlé (3-1-3 ou 4-0-2 sur les mvts composés)' },
  { id: 'more_intense', icon: '\uD83D\uDD25', label: 'Plus intense', phrase: 'Augmente l\u2019intensité globale (RIR 1-2, drop-sets ou rest-pause sur 1-2 mvts/jour)' },
];
const QUICK_DIRECTIVE_CHIPS_EN = [
  { id: 'no_jumps', icon: '🚫', label: 'No jumps', phrase: 'No jumping or plyometrics' },
  { id: 'no_impact', icon: '🚫', label: 'No impact', phrase: 'No impact movements (low-impact only)' },
  { id: 'no_pushups', icon: '🚫', label: 'No push-ups', phrase: 'No push-ups (bench/cable variants are fine)' },
  { id: 'no_burpees', icon: '🚫', label: 'No burpees', phrase: 'No burpees' },
  { id: 'focus_core', icon: '💪', label: 'Focus core', phrase: 'Emphasize core work (anti-rotation, planks, transverse)' },
  { id: 'more_legs', icon: '🦵', label: 'More legs', phrase: 'Increase leg volume (at least 1 dedicated day + 1 leg move per day)' },
  { id: 'slow_tempo', icon: '\u23F1\uFE0F', label: 'Slow tempo', phrase: 'Use a slow controlled tempo (3-1-3 or 4-0-2 on compounds)' },
  { id: 'more_intense', icon: '\uD83D\uDD25', label: 'More intense', phrase: 'Increase global intensity (RIR 1-2, drop-sets or rest-pause on 1-2 moves/day)' },
];

export default function QuickDirectiveChips({ value, onChange, language }) {
  const fr = language === 'fr';
  const chips = fr ? QUICK_DIRECTIVE_CHIPS_FR : QUICK_DIRECTIVE_CHIPS_EN;
  const lowered = (value || '').toLowerCase();
  const isActive = (phrase) => lowered.includes(phrase.toLowerCase());

  const toggle = (phrase) => {
    const current = value || '';
    if (isActive(phrase)) {
      // Remove the phrase + any leading separator (". " / "\n") that we
      // added when injecting it. Falls back to a plain strip if not found.
      const variants = [`. ${phrase}`, `${phrase}. `, `\n${phrase}`, `${phrase}\n`, phrase];
      let next = current;
      for (const v of variants) {
        if (next.toLowerCase().includes(v.toLowerCase())) {
          const idx = next.toLowerCase().indexOf(v.toLowerCase());
          next = next.slice(0, idx) + next.slice(idx + v.length);
          break;
        }
      }
      onChange(next.replace(/\s{2,}/g, ' ').trim());
    } else {
      const trimmed = current.trim();
      const sep = !trimmed ? '' : (/[.!?]$/.test(trimmed) ? ' ' : '. ');
      onChange(`${trimmed}${sep}${phrase}`);
    }
  };

  return (
    <div
      className="flex flex-wrap gap-1.5 mb-2"
      data-testid="quick-directive-chips"
      role="group"
      aria-label={fr ? 'Directives rapides' : 'Quick directives'}
    >
      {chips.map((c) => {
        const active = isActive(c.phrase);
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => toggle(c.phrase)}
            data-testid={`chip-${c.id}`}
            data-active={active ? 'true' : 'false'}
            aria-pressed={active}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all border ${
              active
                ? 'bg-cyan-500/20 border-cyan-400/60 text-cyan-100 shadow-[0_0_10px_rgba(34,211,238,0.35)]'
                : 'bg-secondary/40 border-border/50 text-muted-foreground hover:text-foreground hover:border-cyan-400/40'
            }`}
          >
            <span aria-hidden="true">{c.icon}</span>
            <span>{c.label}</span>
            {active && <span className="text-cyan-300 ml-0.5" aria-hidden="true">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DraftPlanPreview — Iter. 406.fr.25 (Yuri bug : "on ne voit pas le plan
// avant de l'accepter").
// The Armurerie backend returns a multi-week structure
//   { plan_name, weeks: [{ week, days: [{ name, warmup[], exercises[],
//     cooldown[] }] }] }
// but the legacy render path expected a single-session shape
//   { warmup[], main_workout[], cooldown[] }
// → 0 exercise rendered, user clicks "Sauvegarder" blind.
//
// This preview walks the actual structure and lets the user:
//   • voir CHAQUE jour de la semaine 1 (et naviguer aux semaines suivantes)
//   • retirer un exercice de la séance avant activation (✕ Retirer)
//   • lire les notes IA (sécurité, blessures, etc.)
// ─────────────────────────────────────────────────────────────────────
